import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  useFloating,
  offset,
  flip,
  shift,
  autoUpdate,
} from "@floating-ui/react";
import { 
  PiUsersThree
} from "react-icons/pi";
/** For measuring pointer distance */
function getDistance(x1, y1, x2, y2) {
  return Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2);
}

/** Flatten multiple arrays */
function flattenGroups(...arrs) {
  return arrs.flat();
}

/**
 * A "faux checkbox" approach:
 * - For pointer usage, we do single-click or drag to select multiple.
 * - For keyboard usage, we do focus + Space/Enter to toggle.
 */
export default function StudentGroupsSelector({
  checkedGroups,
  onChange,       // toggles a single group
  onSelectAll,
  onDeselectAll,
  onToggleSort,
  sortAlphabetical = false, // true = A-Z, false = by value
}) {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef(null);
  const popoverRef = useRef(null);

  // === DRAG STATE ===
  const [pointerDown, setPointerDown] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragMode, setDragMode] = useState(null); // "select" | "unselect"
  const [startX, setStartX] = useState(0);
  const [startY, setStartY] = useState(0);
  const [initialGroup, setInitialGroup] = useState(null);
  const [initialGroupWasSelected, setInitialGroupWasSelected] = useState(false);

  const visitedGroupsRef = useRef(new Set());

  // === Floating UI for position ===
  const { x, y, strategy, refs } = useFloating({
    placement: "bottom-end",
    middleware: [offset(8), flip(), shift({ padding: 5 })],
    whileElementsMounted: autoUpdate,
  });

  // === TYPE-AHEAD / KEYBOARD STATES ===
  const [typeAheadBuffer, setTypeAheadBuffer] = useState("");
  const [lastTypeTime, setLastTypeTime] = useState(0);

  // Hard-coded columns
  const genderAll = ["All Students", "Male", "Female", "Gender X"];
  const raceEthnicity = [
    "White",
    "Hispanic/ Latino of any race(s)",
    "Two or More Races",
    "Asian",
    "Black/ African American",
    "Native Hawaiian/ Other Pacific Islander",
    "American Indian/ Alaskan Native",
  ];
  const specialPopulations = [
    "Low-Income",
    "Students with Disabilities",
    "Homeless",
    "English Language Learners",
    "Migrant",
    "Military Parent",
    "Section 504",
    "Highly Capable",
  ];
  const nonGroups = [
    "Non-Low Income",
    "Students without Disabilities",
    "Non-Homeless",
    "Non-English Language Learners",
    "Non Migrant",
    "Non Military Parent",
    "Non Section 504",
    "Non-Highly Capable",
  ];

  const allGroupsFlattened = flattenGroups(
    genderAll,
    raceEthnicity,
    specialPopulations,
    nonGroups
  );

  // We STILL keep references for each item for keyboard focus management
  const refsForAllGroups = useMemo(() => {
    return allGroupsFlattened.map(() => React.createRef());
    // eslint-disable-next-line
  }, [allGroupsFlattened.length]);

  // --- Outside click => close ---
  useEffect(() => {
    function handleClickOutside(e) {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // --- End drag if pointer up outside ---
  useEffect(() => {
    const handlePointerUpOutside = () => {
      if (pointerDown) {
        endDrag();
      }
    };
    window.addEventListener("pointerup", handlePointerUpOutside);
    window.addEventListener("pointercancel", handlePointerUpOutside);
    return () => {
      window.removeEventListener("pointerup", handlePointerUpOutside);
      window.removeEventListener("pointercancel", handlePointerUpOutside);
    };
  }, [pointerDown]);

  // --- Auto-focus popover ---
  useEffect(() => {
    if (isOpen && popoverRef.current) {
      popoverRef.current.setAttribute("role", "dialog");
      popoverRef.current.setAttribute("aria-modal", "false");

      setTimeout(() => {
        popoverRef.current?.focus({ preventScroll: true });
      }, 100);
    }
  }, [isOpen]);

  // ---------------------------------------------------------------------------
  // DRAG & SINGLE-CLICK
  // ---------------------------------------------------------------------------

  function handlePointerDown(e) {
    if (e.pointerType !== "mouse" && e.pointerType !== "touch") return;

    const groupEl = getGroupElement(e.target);
    if (groupEl) {
      e.preventDefault(); // block default so there's no "native" click
      const groupName = groupEl.dataset.group || null;

      visitedGroupsRef.current.clear();
      setPointerDown(true);
      setIsDragging(false);

      if (groupName) {
        setInitialGroup(groupName);
        setInitialGroupWasSelected(checkedGroups.includes(groupName));
      } else {
        setInitialGroup(null);
        setInitialGroupWasSelected(false);
      }

      setStartX(e.clientX);
      setStartY(e.clientY);
    }
  }

  function handlePointerMove(e) {
    if (!pointerDown) return;
    if (!isDragging) {
      const dist = getDistance(startX, startY, e.clientX, e.clientY);
      if (dist > 5 && initialGroup) {
        // Now it's a drag
        setIsDragging(true);
        setDragMode(initialGroupWasSelected ? "unselect" : "select");
        visitedGroupsRef.current.clear();
        visitedGroupsRef.current.add(initialGroup);
        setOne(initialGroup, !initialGroupWasSelected);
      } else {
        return; // Not yet dragging
      }
    }

    // If dragging
    const el = document.elementFromPoint(e.clientX, e.clientY);
    const groupEl = getGroupElement(el);
    if (groupEl) {
      const groupName = groupEl.dataset.group;
      if (groupName && !visitedGroupsRef.current.has(groupName)) {
        visitedGroupsRef.current.add(groupName);
        setOne(groupName, dragMode === "select");
      }
    }
  }

  function handlePointerUp() {
    if (!pointerDown) return;
    if (isDragging) {
      endDrag();
    } else {
      // single click
      if (initialGroup) {
        const wasSelected = checkedGroups.includes(initialGroup);
        setOne(initialGroup, !wasSelected);
      }
      endDrag();
    }
  }

  function endDrag() {
    setPointerDown(false);
    setIsDragging(false);
    setDragMode(null);
    visitedGroupsRef.current.clear();
  }

  function getGroupElement(el) {
    let node = el;
    while (node) {
      if (node.dataset && node.dataset.group) return node;
      node = node.parentElement;
    }
    return null;
  }

  const setOne = useCallback((group, shouldSelect) => {
    const isSelected = checkedGroups.includes(group);
    if (shouldSelect && !isSelected) {
      onChange(group);
    } else if (!shouldSelect && isSelected) {
      onChange(group);
    }
  }, [checkedGroups, onChange]);

  // ---------------------------------------------------------------------------
  // KEYBOARD: Type-ahead, focus trap, ESC => close, Space/Enter => toggle
  // ---------------------------------------------------------------------------

  const handleKeyDown = useCallback(
    (e) => {
      const activeEl = document.activeElement;
      const allFocusable = refsForAllGroups.map((r) => r.current);

      // ESC => close
      if (e.key === "Escape") {
        e.preventDefault();
        setIsOpen(false);
        return;
      }

      // Type-ahead (skip space)
      if (
        e.key.length === 1 &&
        e.key !== " " &&
        /\w|\d/.test(e.key)
      ) {
        const now = Date.now();
        if (now - lastTypeTime > 500) {
          setTypeAheadBuffer(e.key);
        } else {
          setTypeAheadBuffer((prev) => prev + e.key);
        }
        setLastTypeTime(now);

        requestAnimationFrame(() => {
          const typed =
            now - lastTypeTime > 500
              ? e.key
              : typeAheadBuffer + e.key;
          const lower = typed.toLowerCase();
          // find first group containing typed substring
          const index = allGroupsFlattened.findIndex((g) =>
            g.toLowerCase().includes(lower)
          );
          if (index >= 0 && refsForAllGroups[index].current) {
            refsForAllGroups[index].current.focus();
          }
        });
        return;
      }

      // Space/Enter => toggle the currently focused "faux checkbox"
      if (e.key === " " || e.key === "Enter") {
        if (activeEl && activeEl.dataset && activeEl.dataset.group) {
          e.preventDefault(); // no scroll
          const groupName = activeEl.dataset.group;
          if (groupName) {
            const isChecked = checkedGroups.includes(groupName);
            setOne(groupName, !isChecked);
          }
        }
      }

      // Focus trap with Tab
      if (e.key === "Tab") {
        const firstItem = allFocusable[0];
        const lastItem = allFocusable[allFocusable.length - 1];

        if (e.shiftKey) {
          // SHIFT+TAB
          if (activeEl === firstItem || !allFocusable.includes(activeEl)) {
            e.preventDefault();
            lastItem?.focus();
          }
        } else {
          // TAB forward
          if (activeEl === lastItem) {
            e.preventDefault();
            firstItem?.focus();
          }
        }
      }
    },
    [
      allGroupsFlattened,
      refsForAllGroups,
      typeAheadBuffer,
      lastTypeTime,
      checkedGroups,
      setOne
    ]
  );

  useEffect(() => {
    if (!isOpen) setTypeAheadBuffer("");
  }, [isOpen]);

  // Renders each group as a "faux checkbox"
  function renderGroupList(groups) {
    return groups.map((group) => {
      const isChecked = checkedGroups.includes(group);
      const indexInAll = allGroupsFlattened.indexOf(group);
      const ref = refsForAllGroups[indexInAll];

      return (
        <div
          key={group}
          data-group={group}
          ref={ref}
          // role="checkbox" for screen readers
          role="checkbox"
          tabIndex={0} // focusable
          aria-checked={isChecked}
          // Added focus-visible ring for keyboard navigation.
          className={`group flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm text-zinc-700 
                      hover:bg-zinc-100 truncate 
                      focus-visible:ring-2 focus-visible:ring-blue-600
                      ${
                        isChecked
                          ? ""
                          : ""
                      }`}
          // We rely on pointerDown / pointerMove / pointerUp from the container
        >
          {/* Animated faux checkbox with hover effect */}
          <motion.div
            transition={{ duration: 0.2 }}
            className={`flex min-h-4 min-w-4 items-center justify-center border rounded transition-all
                        ${
                          isChecked
                            ? "border-transparent bg-blue-600"
                            : "border-zinc-300 bg-white"
                        }`}
          >
            {isChecked && (
              <motion.svg
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 300, damping: 20 }}
                className="h-3 w-3 text-white"
                fill="none"
                stroke="currentColor"
                strokeWidth={3}
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M5 13l4 4L19 7"
                />
              </motion.svg>
            )}
          </motion.div>
          <span className="truncate">{group}</span>
        </div>
      );
    });
  }

  return (
    <div className="flex items-end">
      {/* Trigger Button */}
      <button
      ref={(node) => {
        buttonRef.current = node;
        refs.setReference(node);
      }}
      onClick={() => setIsOpen((prev) => !prev)}
      aria-expanded={isOpen ? "true" : "false"}
      className="
        inline-flex items-center gap-2
        px-4 h-10
        rounded bg-white border-[#cccccc] border
        text-gray-700
        hover:border-[#818cf8]
        transition
        focus:outline-none
      "
    >
      <PiUsersThree className="w-5 h-5 text-gray-700" />
      <span className="font-medium">Filter Student Groups</span>
      <svg
        className={`transition-transform w-4 h-4 ${isOpen ? "rotate-180" : "rotate-0"}`}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    </button>
    {/* Divider placed after the button */}
    <div className="w-[1px] my-4 h-4 bg-zinc-200 ml-2" />

      <AnimatePresence>
        {isOpen && (
          <motion.div
            ref={(node) => {
              popoverRef.current = node;
              refs.setFloating(node);
            }}
            className="z-50 w-[44rem] max-h-[32rem] overflow-hidden
                       rounded-md border border-zinc-200 bg-white
                       shadow-lg ring-1 ring-black ring-opacity-5 select-none
                       outline-none"
            style={{
              position: strategy,
              top: y ?? 0,
              left: x ?? 0,
            }}
            // Animate in/out
            initial={{ opacity: 0, scale: 0.95, y: -5 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -5 }}
            transition={{ duration: 0.15 }}
            // Container-level pointer events for single-click / drag
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            // For keyboard
            tabIndex={-1}
            onKeyDown={handleKeyDown}
          >
            {/* Top Action Buttons */}
<div className="flex items-center justify-between border-b border-zinc-100 px-2 py-2">
  <button
    onClick={onSelectAll}
    tabIndex={-1}
    className="text-xs font-medium text-blue-600 hover:underline focus:outline-none"
  >
    Select All
  </button>
  <button
    onClick={onDeselectAll}
    tabIndex={-1}
    className="text-xs font-medium text-blue-600 hover:underline focus:outline-none"
  >
    Deselect All
  </button>
  <button
    onClick={onToggleSort}
    tabIndex={-1}
    className="text-xs font-medium text-blue-600 hover:underline focus:outline-none"
  >
    Sort: {sortAlphabetical ? "A-Z" : "By Value"}
  </button>
</div>

            {/* 3 Columns */}
            <div className="grid grid-cols-3 gap-4 p-2 max-h-[28rem] overflow-y-auto">
              {/* Column 1 */}
              <div>
                <h3 className="mb-2 text-xs font-semibold uppercase text-zinc-800 tracking-wider">
                  Gender / All
                </h3>
                {renderGroupList(genderAll)}

                <h3 className="mt-4 mb-2 text-xs font-semibold uppercase text-zinc-800 tracking-wider">
                  Race / Ethnicity
                </h3>
                {renderGroupList(raceEthnicity)}
              </div>

              {/* Column 2 */}
              <div>
                <h3 className="mb-2 text-xs font-semibold uppercase text-zinc-800 tracking-wider">
                  Special Populations
                </h3>
                {renderGroupList(specialPopulations)}
              </div>

              {/* Column 3 */}
              <div>
                <h3 className="mb-2 text-xs font-semibold uppercase text-zinc-800 tracking-wider">
                  Non Groups
                </h3>
                {renderGroupList(nonGroups)}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}